// Notes widget (Players canvas) — consumes the picked player (playerId). Shows
// the player's shared notes and lets you add one (the write path: POST
// /player-notes). Notes are plain text wrapped in a Tiptap doc (see noteText).

import { useState } from "react";
import { usePlayerNotes, useCreateNote } from "../data/players.js";
import { noteText } from "../lib/noteText.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";

function formatWhen(iso: string): string {
  const d = iso.slice(0, 10);
  const t = iso.slice(11, 16);
  return t ? `${d} ${t}` : d;
}

export default function NotesWidget({ scope }: { scope: WidgetScope }) {
  const { playerId } = scope;
  const notes = usePlayerNotes(playerId);
  const create = useCreateNote(playerId);
  const [draft, setDraft] = useState("");

  if (playerId === null)
    return <div style={{ color: theme.inkMuted, fontSize: 13 }}>Search for a player above.</div>;

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    create.mutate(text, { onSuccess: () => setDraft("") });
  };

  const list = notes.data?.notes ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note about this player…"
          rows={2}
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: theme.input,
            color: theme.ink,
            border: `1px solid ${theme.borderInput}`,
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 13,
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={submit}
            disabled={create.isPending || draft.trim() === ""}
            style={{
              padding: "6px 14px",
              borderRadius: 8,
              border: "none",
              background: draft.trim() && !create.isPending ? theme.accent : theme.surface,
              color: draft.trim() && !create.isPending ? theme.page : theme.inkMuted,
              fontSize: 13,
              fontWeight: 600,
              cursor: draft.trim() && !create.isPending ? "pointer" : "default",
            }}
          >
            {create.isPending ? "Adding…" : "Add note"}
          </button>
          {create.isError && <span style={{ color: theme.danger, fontSize: 12 }}>Couldn't add.</span>}
        </div>
      </div>

      {notes.isPending ? (
        <div style={{ color: theme.inkMuted, fontSize: 13 }}>Loading notes…</div>
      ) : notes.isError ? (
        <div style={{ color: theme.danger, fontSize: 13 }}>Couldn't load notes.</div>
      ) : list.length === 0 ? (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>No notes yet — add the first.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.map((n) => (
            <div key={n.noteId} style={{ background: theme.field, borderRadius: 8, padding: "8px 10px" }}>
              <div style={{ color: theme.ink, fontSize: 13, whiteSpace: "pre-wrap" }}>{noteText(n.note)}</div>
              <div style={{ color: theme.inkFaint, fontSize: 11, marginTop: 4 }}>
                {n.authorName} · {formatWhen(n.createdAt)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
